// Matches what Liquid's money filter renders for this shop — $1,000.00 — so a
// total this file rewrites doesn't lose the separator the server-rendered price
// had. Grouping is applied to the whole part only.
function formatMoney(cents) {
  const [whole, fraction] = (cents / 100).toFixed(2).split('.');
  return '$' + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + fraction;
}

// "1 DATE SELECTED" / "3 DATES SELECTED". English-only, like the rest of the
// copy this file writes; the count always leads, so no re-ordering is needed.
function countLabel(count, singular, plural) {
  return count + ' ' + (count === 1 ? singular : plural) + ' SELECTED';
}

class ProductDetailAccordion {
  constructor(el) {
    this.el = el;
    this.trigger = el.querySelector('[data-accordion-trigger]');
    this.content = el.querySelector('[data-accordion-content]');

    if (!this.trigger || !this.content) return;

    this.trigger.addEventListener('click', this.toggle.bind(this));
  }

  toggle() {
    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.content.style.maxHeight = this.content.scrollHeight + 'px';
    this.content.classList.add('is-open');
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.content.style.maxHeight = '0';
    this.content.classList.remove('is-open');
  }
}

class ProductDetailReadMore {
  constructor(el) {
    this.el = el;
    this.text = el.querySelector('[data-readmore-text]');
    this.btn = el.querySelector('[data-readmore-btn]');

    if (!this.text || !this.btn) return;

    this.expanded = false;
    this.parentAccordion = el.closest('[data-accordion-content]');
    this.smooth =
      !!el.closest('.product-detail__description-block') ||
      !!el.closest('.store-product__description-block') ||
      !!el.closest('.flash-card-pdp__description-block') ||
      !!el.closest('.guide-book-pdp__description-block') ||
      !!this.parentAccordion;
    this.collapsedMaxHeight = this.text.clientHeight;
    this.btn.addEventListener('click', this.toggle.bind(this));
    this.checkOverflow();
  }

  checkOverflow() {
    if (this.text.scrollHeight <= this.text.clientHeight + 1) {
      this.btn.style.display = 'none';
    }
  }

  toggle() {
    this.expanded = !this.expanded;

    if (this.smooth) {
      this.toggleSmooth();
    } else {
      this.toggleSimple();
    }
  }

  /**
   * Open the parent accordion's clip ceiling to a value tall enough to contain
   * the child during its transition, with the parent's own transition disabled
   * so only the child's animation is visible. The parent has overflow:hidden,
   * so without this it would clip the growing readmore mid-animation, causing
   * the visible "stair-step" effect.
   */
  freezeAccordion(targetMaxHeight) {
    if (!this.parentAccordion || !this.parentAccordion.classList.contains('is-open')) return;
    this.parentAccordion.style.transition = 'none';
    this.parentAccordion.style.maxHeight = targetMaxHeight + 'px';
    // Force reflow so the browser commits the new value before we restore the transition.
    // eslint-disable-next-line no-unused-expressions
    this.parentAccordion.offsetHeight;
  }

  thawAccordion() {
    if (!this.parentAccordion || !this.parentAccordion.classList.contains('is-open')) return;
    this.parentAccordion.style.transition = '';
    this.parentAccordion.style.maxHeight = this.parentAccordion.scrollHeight + 'px';
  }

  toggleSimple() {
    if (this.expanded) {
      this.text.classList.add('is-expanded');
      this.btn.textContent = 'Read Less';
    } else {
      this.text.classList.remove('is-expanded');
      this.btn.textContent = 'Read More';
    }
    this.thawAccordion();
  }

  toggleSmooth() {
    const target = this.text.scrollHeight;

    if (this.expanded) {
      this.text.classList.add('is-expanded');
      this.btn.textContent = 'Read Less';
      // Open the parent's ceiling instantly to fit the future child height, so the
      // child's smooth transition is the only motion the user sees.
      const parentTarget = this.parentAccordion
        ? this.parentAccordion.scrollHeight + (target - this.collapsedMaxHeight) + 100
        : 0;
      this.freezeAccordion(parentTarget);
      this.text.style.maxHeight = target + 'px';
      const onEnd = (e) => {
        if (e.propertyName !== 'max-height' || e.target !== this.text) return;
        this.text.style.maxHeight = 'none';
        this.thawAccordion();
        this.text.removeEventListener('transitionend', onEnd);
      };
      this.text.addEventListener('transitionend', onEnd);
    } else {
      this.text.style.maxHeight = target + 'px';
      // Force reflow so the browser registers the explicit max-height before we shrink it.
      // eslint-disable-next-line no-unused-expressions
      this.text.offsetHeight;
      // Keep the parent frozen at its current expanded height during the child collapse,
      // so the parent doesn't shrink mid-motion and clip the child.
      this.freezeAccordion(this.parentAccordion ? this.parentAccordion.scrollHeight : 0);
      this.text.style.maxHeight = this.collapsedMaxHeight + 'px';
      this.btn.textContent = 'Read More';
      const onEnd = (e) => {
        if (e.propertyName !== 'max-height' || e.target !== this.text) return;
        this.text.style.maxHeight = '';
        this.text.classList.remove('is-expanded');
        this.thawAccordion();
        this.text.removeEventListener('transitionend', onEnd);
      };
      this.text.addEventListener('transitionend', onEnd);
    }
  }
}

class ProductDetailVariantSelector {
  constructor(section) {
    this.section = section;
    this.variantSelector = section.querySelector('[data-variant-selector]');
    if (!this.variantSelector) return;

    this.options = this.variantSelector.querySelectorAll('[data-variant-option]');
    this.priceBlock = section.querySelector('.product-detail__price-block');
    this.priceDisplay = this.priceBlock ? this.priceBlock.querySelector('.product-detail__price-sale') : null;
    this.comparePriceDisplay = this.priceBlock ? this.priceBlock.querySelector('.product-detail__price-compare') : null;
    this.addToCartBtn = section.querySelector('.product-detail__register-btn');
    this.selectDatesBtn = section.querySelector('[data-select-dates-trigger]');
    this.pickDatesOption = this.variantSelector.querySelector('[data-variant-option="pick-dates"]');

    // Store original price from the "next available" option card
    const nextAvailableOption = this.variantSelector.querySelector('[data-variant-option="next-available"]');
    this.originalPriceText = nextAvailableOption
      ? nextAvailableOption.querySelector('.variant-selector__price').textContent.trim()
      : this.priceDisplay
      ? this.priceDisplay.textContent.trim()
      : '$0.00';

    // Initialize date picker popup
    this.datePicker = new ProductDetailDatePicker(section, this);

    // Set initial data-selected-variants for the default selected option
    if (this.addToCartBtn && nextAvailableOption) {
      const variantId = nextAvailableOption.getAttribute('data-variant-id');
      if (variantId) {
        this.addToCartBtn.setAttribute('data-selected-variants',
          JSON.stringify([{ id: variantId, quantity: 1 }])
        );
      }
    }

    this.bindEvents();

    // Without the next-available option there's nothing to seed the initial
    // price and button state, so run the pick-dates selection once: it shows
    // $0.00, disables Add to cart and clears the variant payload until dates
    // are actually chosen.
    if (!nextAvailableOption && this.pickDatesOption) {
      this.selectOption(this.pickDatesOption);
    }
  }

  bindEvents() {
    // Add click handlers to each option
    this.options.forEach((option) => {
      option.addEventListener('click', (e) => {
        // If clicking on CHOOSE DATES link, let that handler deal with it
        if (e.target.closest('[data-select-dates-trigger]')) {
          return;
        }
        this.selectOption(option);
      });
    });

    // Handle CHOOSE DATES button click
    if (this.selectDatesBtn) {
      this.selectDatesBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Find and select the "pick dates" option
        const pickDatesOption = this.variantSelector.querySelector('[data-variant-option="pick-dates"]');
        if (pickDatesOption) {
          this.selectOption(pickDatesOption);
        }

        // Open date picker popup
        this.datePicker.open();
      });
    }
  }

  selectOption(optionToSelect) {
    // Remove is-selected from all options first
    this.options.forEach((opt) => {
      opt.classList.remove('is-selected');
    });

    // Add is-selected to the target option
    optionToSelect.classList.add('is-selected');

    // Get the option type
    const optionType = optionToSelect.getAttribute('data-variant-option');

    // Update price and button based on selection
    if (optionType === 'pick-dates') {
      // Clear cart-added state when switching to pick-dates
      if (this.addToCartBtn) {
        this.addToCartBtn.classList.remove('is-added-to-cart');
        const arrowEl = this.addToCartBtn.querySelector('.button__icon');
        if (arrowEl) arrowEl.style.display = '';
      }

      // Check if dates have already been applied
      const appliedPrice = optionToSelect.getAttribute('data-total-price');

      if (appliedPrice && parseInt(appliedPrice, 10) > 0) {
        // Dates were applied - show applied price and enable button
        if (this.addToCartBtn) {
          this.addToCartBtn.classList.remove('is-disabled');
        }
        if (this.priceDisplay) {
          this.priceDisplay.textContent = formatMoney(parseInt(appliedPrice, 10));
        }
      } else {
        // No dates applied yet - disable button and show $0.00
        if (this.addToCartBtn) {
          this.addToCartBtn.classList.add('is-disabled');
        }
        if (this.priceDisplay) {
          this.priceDisplay.textContent = '$0.00';
        }
      }
      if (this.comparePriceDisplay) {
        this.comparePriceDisplay.style.display = 'none';
      }
      // Set data-selected-variants for pick-dates
      if (this.addToCartBtn) {
        const variantIds = optionToSelect.getAttribute('data-variant-ids');
        if (variantIds) {
          const ids = JSON.parse(variantIds);
          this.addToCartBtn.setAttribute('data-selected-variants',
            JSON.stringify(ids.map(id => ({ id: id, quantity: 1 })))
          );
        } else {
          // No dates applied yet — clear the payload so a leftover single-variant
          // selection (e.g. the next-available id) isn't mistaken for something to add.
          this.addToCartBtn.setAttribute('data-selected-variants', '[]');
        }
      }
    } else if (optionType === 'next-available') {
      // Enable add to cart and restore original price
      if (this.addToCartBtn) {
        this.addToCartBtn.classList.remove('is-disabled');
        this.addToCartBtn.classList.remove('is-added-to-cart');
        const arrowEl = this.addToCartBtn.querySelector('.button__icon');
        if (arrowEl) arrowEl.style.display = '';
      }
      if (this.priceDisplay) {
        this.priceDisplay.textContent = this.originalPriceText;
      }
      if (this.comparePriceDisplay) {
        this.comparePriceDisplay.style.display = '';
      }
      // Set data-selected-variants for next-available (single variant)
      if (this.addToCartBtn) {
        const variantId = optionToSelect.getAttribute('data-variant-id');
        if (variantId) {
          this.addToCartBtn.setAttribute('data-selected-variants',
            JSON.stringify([{ id: variantId, quantity: 1 }])
          );

          // Re-check cart state
          if (window.cartStateManager && optionToSelect.classList.contains('is-in-cart')) {
            window.cartStateManager.disableButton(this.addToCartBtn, 'ADDED TO CART');
          }
        }
      }
    }
  }

  // Called from date picker when dates are applied.
  // - selectedVariants: only the NEW (not-yet-in-cart) dates → price + add-to-cart payload
  // - totalCount: full number of checked dates (incl. already-in-cart) → shown on the card
  applySelectedDates(selectedVariants, totalPrice, totalCount) {
    const count = typeof totalCount === 'number' ? totalCount : selectedVariants.length;

    // Update main price display (TOTAL PRICE section)
    if (this.priceDisplay) {
      this.priceDisplay.textContent = formatMoney(totalPrice);
    }

    // Store selected variant IDs on add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.classList.remove('is-disabled');
      this.addToCartBtn.setAttribute('data-selected-variants',
        JSON.stringify(selectedVariants.map(v => ({ id: v.id, quantity: 1 })))
      );

      // `selectedVariants` here are only the NEW (not-yet-in-cart) dates, so the
      // button must read "ADD TO CART" — even if some other picked dates are
      // already in the cart. It only becomes "ADDED TO CART" once every picked
      // date is in the cart (handled by CartStateManager after the add succeeds).
      this.addToCartBtn.classList.remove('is-added-to-cart');
      const textEl = this.addToCartBtn.querySelector('.button__text');
      if (textEl) textEl.textContent = 'ADD TO CART';
      const arrowEl = this.addToCartBtn.querySelector('.button__icon');
      if (arrowEl) arrowEl.style.display = '';
    }

    // Update the pick dates option
    const pickDatesOption = this.variantSelector.querySelector('[data-variant-option="pick-dates"]');
    if (pickDatesOption) {
      pickDatesOption.setAttribute('data-variant-ids', JSON.stringify(selectedVariants.map(v => v.id)));
      pickDatesOption.setAttribute('data-total-price', totalPrice);

      // Update the price display in pick dates option
      const pickDatesPrice = pickDatesOption.querySelector('.variant-selector__price');
      if (pickDatesPrice) {
        pickDatesPrice.textContent = formatMoney(totalPrice);
      }

      // Update CHOOSE DATES text to show number of selected classes
      const selectDatesLink = pickDatesOption.querySelector('[data-select-dates-trigger]');
      if (selectDatesLink) {
        selectDatesLink.textContent = count > 0
          ? countLabel(count, 'DATE', 'DATES')
          : 'CHOOSE DATES';
      }
    }
  }
}

// Pure pricing maths — no DOM. Turns metafield tier lines ("5|55") + a selected
// count into a pricing state. Banner is informational; discounts are Shopify's.
class DynamicPricingEngine {
  constructor(rawTiers) {
    this.tiers = this.parseTiers(rawTiers);
  }

  // ["5|55", ...] → sorted [{ minQty, price, priceDisplay }]; bad lines dropped.
  parseTiers(rawTiers) {
    if (!Array.isArray(rawTiers)) return [];
    return rawTiers
      .map((line) => this.parseLine(line))
      .filter(Boolean)
      .sort((tierA, tierB) => tierA.minQty - tierB.minQty);
  }

  // "5|55" → { minQty:5, price:55, priceDisplay:"$55" }, or null if malformed.
  parseLine(line) {
    if (typeof line !== 'string') return null;
    const parts = line.split('|');
    if (parts.length < 2) return null;
    const minQty = parseInt(parts[0].trim(), 10);
    const price = parseFloat(parts[1].trim().replace(/^\$/, '').replace(/,/g, ''));
    if (!Number.isFinite(minQty) || !Number.isFinite(price)) return null;
    return { minQty, price, priceDisplay: this.formatPrice(price) };
  }

  formatPrice(value) {
    return Number.isInteger(value) ? `$${value}` : formatMoney(value * 100);
  }

  hasTiers() {
    return this.tiers.length > 0;
  }

  evaluate(count) {
    // Highest tier the count has reached, then the first tier still above it.
    let current = null;
    for (const tier of this.tiers) {
      if (count >= tier.minQty) {
        current = tier;
      } else {
        break;
      }
    }
    const next = this.tiers.find((tier) => tier.minQty > count) || null;

    return {
      count,
      current,
      next,
      remaining: next ? next.minQty - count : 0,
    };
  }
}

class ProductDetailDatePicker {
  constructor(section, variantSelector) {
    this.section = section;
    this.variantSelector = variantSelector;
    this.overlay = section.querySelector('[data-date-picker-overlay]');
    this.popup = section.querySelector('[data-date-picker-popup]');

    if (!this.popup) return;

    this.closeBtn = this.popup.querySelector('[data-date-picker-close]');
    this.monthsContainer = this.popup.querySelector('[data-date-picker-months]');
    this.variantsContainer = this.popup.querySelector('[data-date-picker-variants]');
    this.totalPriceDisplay = this.popup.querySelector('[data-total-price]');
    this.selectedCountDisplay = this.popup.querySelector('[data-selected-count]');
    this.applyBtn = this.popup.querySelector('#date-picker-apply-btn');

    // Dynamic pricing banner — tiers read from the shop metafield. Renderer is
    // inline here (a couple of textContent writes); the maths lives in the engine.
    this.pricingBannerEl = this.popup.querySelector('[data-dynamic-pricing-banner]');
    this.pricingMainEl = this.pricingBannerEl
      ? this.pricingBannerEl.querySelector('[data-dynamic-pricing-main]')
      : null;
    this.pricingHintEl = this.pricingBannerEl
      ? this.pricingBannerEl.querySelector('[data-dynamic-pricing-hint]')
      : null;
    this.pricingEngine = new DynamicPricingEngine(this.parsePricingTiers());
    // Merchant-authored banner copy keyed by threshold ("0","5",…,"N"). Empty → hardcoded fallback.
    this.pricingText = this.parsePricingText();

    this.selectedVariants = [];
    this.allVariants = [];
    this.monthTabs = [];

    this.init();
  }

  init() {
    if (!this.variantsContainer || !this.monthsContainer) return;

    // Parse all variants from the DOM
    this.parseVariants();

    // Setup month tabs based on available variants
    this.setupMonthTabs();

    // Bind events
    this.bindEvents();
  }

  parseVariants() {
    const variantItems = this.variantsContainer.querySelectorAll('[data-variant-id]');
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    variantItems.forEach((item) => {
      const variantTitle = item.getAttribute('data-variant-title');
      const variantId = item.getAttribute('data-variant-id');
      const variantPrice = parseInt(item.getAttribute('data-variant-price'), 10);
      const variantInventory = parseInt(item.getAttribute('data-variant-inventory'), 10);
      const variantAvailable = item.getAttribute('data-variant-available') === 'true';

      // Parse month from variant title (e.g., "Jan 05, 2026" or "January 5, 2026")
      let variantMonth = '';
      const titleLower = variantTitle.toLowerCase();

      // Try to match month name in title
      for (let i = 0; i < monthNames.length; i++) {
        if (titleLower.includes(monthNames[i].toLowerCase()) ||
            titleLower.includes(this.getFullMonthName(i).toLowerCase())) {
          variantMonth = monthNames[i];
          break;
        }
      }

      // If no month found, try to parse date format (e.g., "2026-03-15" or date object)
      if (!variantMonth) {
        const dateMatch = variantTitle.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const monthIndex = parseInt(dateMatch[2], 10) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            variantMonth = monthNames[monthIndex];
          }
        }
      }

      this.allVariants.push({
        id: variantId,
        title: variantTitle,
        price: variantPrice,
        inventory: variantInventory,
        available: variantAvailable,
        month: variantMonth,
        element: item
      });
    });
  }

  getFullMonthName(index) {
    const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return fullMonths[index];
  }

  setupMonthTabs() {
    const monthTabs = this.monthsContainer.querySelectorAll('[data-month]');
    const variantMonths = new Set(this.allVariants.map(v => v.month));

    monthTabs.forEach((tab) => {
      const month = tab.getAttribute('data-month');

      // Show only months that have variants
      if (variantMonths.has(month)) {
        tab.style.display = '';
      } else {
        // Hide months without variants
        tab.style.display = 'none';
      }

      this.monthTabs.push(tab);
    });

    // Set first visible tab as active
    const firstVisibleTab = this.monthTabs.find(tab => tab.style.display !== 'none');
    if (firstVisibleTab) {
      this.monthTabs.forEach(t => t.classList.remove('is-active'));
      firstVisibleTab.classList.add('is-active');
      this.filterVariantsByMonth(firstVisibleTab.getAttribute('data-month'));
    }
  }

  // Update month tabs to show dots for months with selected variants
  updateMonthTabsDots() {
    // Get months that have selected variants
    const selectedMonths = new Set(this.selectedVariants.map(v => v.month));

    this.monthTabs.forEach((tab) => {
      const month = tab.getAttribute('data-month');
      if (selectedMonths.has(month)) {
        tab.classList.add('has-selected');
      } else {
        tab.classList.remove('has-selected');
      }
    });
  }

  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Month tabs
    this.monthTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.monthTabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        this.filterVariantsByMonth(tab.getAttribute('data-month'));
      });
    });

    // Variant item clicks
    this.allVariants.forEach((variant) => {
      variant.element.addEventListener('click', () => {
        this.toggleVariantSelection(variant);
      });
    });

    // Apply button
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', () => {
        this.applySelection();
      });
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Update pricing banner when cart conversation-lab count changes
    document.addEventListener('cart:conversation-lab-count-updated', () => {
      if (this.isOpen()) this.updatePricingBanner();
    });
  }

  filterVariantsByMonth(month) {
    this.allVariants.forEach((variant) => {
      if (variant.month === month) {
        variant.element.style.display = '';
      } else {
        variant.element.style.display = 'none';
      }
    });
  }

  toggleVariantSelection(variant) {
    // Variants already in the cart are locked — they stay checked and counted,
    // but the user can't toggle them off (they're already purchased/added).
    if (variant.element.classList.contains('is-in-cart')) return;

    const index = this.selectedVariants.findIndex(v => v.id === variant.id);

    if (index > -1) {
      // Deselect - allow full deselect (0 selected)
      this.selectedVariants.splice(index, 1);
      variant.element.classList.remove('is-selected');
    } else {
      // Select
      this.selectedVariants.push(variant);
      variant.element.classList.add('is-selected');
    }

    const activeVariants = this.getActiveVariants();
    this.updatePriceDisplay(activeVariants);
    this.updateMonthTabsDots();
    this.updateMainPriceAndButton(activeVariants);
  }

  // Pre-select every variant that is already in the cart so it shows as checked
  // and is included in the selected-class count. These stay locked (see
  // toggleVariantSelection) and don't contribute to the price/add-to-cart
  // payload (see the active-variant filters below), since re-adding them would
  // duplicate the line item.
  syncInCartSelection() {
    this.allVariants.forEach((variant) => {
      const inCart = variant.element && variant.element.classList.contains('is-in-cart');
      if (!inCart) return;
      variant.element.classList.add('is-selected');
      if (!this.selectedVariants.some(v => v.id === variant.id)) {
        this.selectedVariants.push(variant);
      }
    });

    const activeVariants = this.getActiveVariants();
    this.updatePriceDisplay(activeVariants);
    this.updateMonthTabsDots();
    this.updateMainPriceAndButton(activeVariants);
  }

  // Selected dates the shopper hasn't added to the cart yet — these drive the
  // price total, the CONFIRM SELECTION button, and the add-to-cart payload. Dates already in
  // the cart stay checked and counted, but are excluded here (re-adding them
  // would duplicate the line item).
  getActiveVariants() {
    return this.selectedVariants.filter(
      (variant) => !(variant.element && variant.element.classList.contains('is-in-cart'))
    );
  }

  updatePriceDisplay(activeVariants = this.getActiveVariants()) {
    const totalPrice = activeVariants.reduce((sum, variant) => sum + variant.price, 0);
    // Count = everything checked, including dates already added to the cart, so
    // the shopper sees the full number of classes they'll have selected.
    const count = this.selectedVariants.length;
    const activeCount = activeVariants.length;

    if (this.totalPriceDisplay) {
      this.totalPriceDisplay.textContent = formatMoney(totalPrice);
    }

    if (this.selectedCountDisplay) {
      this.selectedCountDisplay.textContent = countLabel(count, 'SESSION', 'SESSIONS');
    }

    // Enable CONFIRM SELECTION only when there's at least one new (not-yet-in-cart) date to apply
    if (this.applyBtn) {
      if (activeCount === 0) {
        this.applyBtn.classList.add('is-disabled');
      } else {
        this.applyBtn.classList.remove('is-disabled');
      }
    }

    // Banner reflects the cart-wide conversation-lab count, not just this product.
    this.updatePricingBanner(activeVariants);
  }

  // Sessions that count toward package pricing: conversation-lab items already in
  // the cart (cart-wide — every conversation-lab PDP) plus the new dates picked in
  // this modal that aren't in the cart yet. Mirrors what Shopify prices against.
  getDynamicPricingCount(activeVariants = this.getActiveVariants()) {
    const newlyPicked = activeVariants.length;
    const alreadyInCart = window.cartStateManager
      ? window.cartStateManager.getConversationLabCount()
      : 0;
    return alreadyInCart + newlyPicked;
  }

  // Tier lines from the banner's data attribute; [] if absent/invalid.
  parsePricingTiers() {
    if (!this.pricingBannerEl) return [];
    const raw = this.pricingBannerEl.getAttribute('data-pricing-tiers');
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (error) {
      return [];
    }
  }

  // Merchant banner copy → { threshold: template }. e.g. "5|Add [count] more…" → {"5":"Add [count] more…"}.
  // Split on the FIRST "|" only so the copy itself may contain the character. {} if absent/invalid.
  parsePricingText() {
    if (!this.pricingBannerEl) return {};
    const raw = this.pricingBannerEl.getAttribute('data-pricing-text');
    if (!raw) return {};
    let lines;
    try {
      lines = JSON.parse(raw);
    } catch (error) {
      return {};
    }
    if (!Array.isArray(lines)) return {};
    const map = {};
    lines.forEach((line) => {
      if (typeof line !== 'string') return;
      const i = line.indexOf('|');
      if (i === -1) return;
      const key = line.slice(0, i).trim();
      if (key) map[key] = line.slice(i + 1).trim();
    });
    return map;
  }

  // Merchant line for the current state with [count]/[price] filled, or null → hardcoded fallback.
  // State→key: count 0 → "0" (static, no tokens); below/between tiers → next tier's minQty; maxed → "N".
  // [price] = the tier the sentence is about = (current || next), as a bare number (merchant writes the $).
  getCustomPricingText(count, current, next, remaining) {
    let key;
    if (count === 0) key = '0';
    else if (next) key = String(next.minQty);
    else key = 'N';

    const template = this.pricingText[key];
    if (template == null) return null;
    if (key === '0') return template;

    const priceTier = current || next;
    const priceStr = priceTier ? priceTier.priceDisplay.replace(/^\$/, '') : '';
    return template
      .split('[count]')
      .join(remaining != null ? remaining : '')
      .split('[price]')
      .join(priceStr);
  }

  // Writes the engine's state to the banner DOM. No maths here.
  updatePricingBanner(activeVariants = this.getActiveVariants()) {
    if (!this.pricingBannerEl || !this.pricingMainEl || !this.pricingHintEl) return;
    if (!this.pricingEngine.hasTiers()) {
      this.pricingBannerEl.hidden = true;
      return;
    }

    const sessionCount = this.getDynamicPricingCount(activeVariants);
    const { current, next, remaining } = this.pricingEngine.evaluate(sessionCount);

    // Merchant copy only. A state the merchant left unwritten is deliberately
    // silent, so no line for it means no banner rather than theme wording.
    const text = this.getCustomPricingText(sessionCount, current, next, remaining);
    if (text === null) {
      this.pricingBannerEl.hidden = true;
      return;
    }
    this.pricingBannerEl.hidden = false;

    // A [br] token splits the line into main + second (hint) line.
    const parts = text.split('[br]');
    this.pricingMainEl.textContent = parts[0].trim();
    this.pricingHintEl.textContent = parts.slice(1).join(' ').trim();
    this.pricingBannerEl.classList.toggle('is-applied', !!current);
  }

  // Update main PDP price and add-to-cart button in real-time as variants are checked/unchecked
  updateMainPriceAndButton(activeVariants = this.getActiveVariants()) {
    const totalPrice = activeVariants.reduce((sum, variant) => sum + variant.price, 0);
    const count = activeVariants.length;
    const priceDisplay = this.variantSelector.priceDisplay;
    const addToCartBtn = this.variantSelector.addToCartBtn;
    const formatted = count > 0 ? formatMoney(totalPrice) : '$0.00';

    if (count > 0) {
      // Variants selected - show total price and enable add to cart
      if (priceDisplay) {
        priceDisplay.textContent = formatted;
      }
      if (addToCartBtn) {
        addToCartBtn.classList.remove('is-disabled');
        addToCartBtn.setAttribute('data-selected-variants',
          JSON.stringify(activeVariants.map(v => ({ id: v.id, quantity: 1 })))
        );
        // New dates were picked → reset the button out of the "ADDED TO CART"
        // state so it reads ADD TO CART again for the fresh selection.
        if (addToCartBtn.classList.contains('is-added-to-cart')) {
          addToCartBtn.classList.remove('is-added-to-cart');
          const textEl = addToCartBtn.querySelector('.button__text');
          if (textEl) textEl.textContent = 'ADD TO CART';
          const arrowEl = addToCartBtn.querySelector('.button__icon');
          if (arrowEl) arrowEl.style.display = '';
        }
      }
    } else {
      // No variants selected - show $0.00 and disable add to cart
      if (priceDisplay) {
        priceDisplay.textContent = formatted;
      }
      if (addToCartBtn) {
        addToCartBtn.classList.add('is-disabled');
        addToCartBtn.setAttribute('data-selected-variants', '[]');
      }
    }
  }

  open() {
    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }
    if (this.popup) {
      this.popup.classList.add('is-active');
    }
    document.body.style.overflow = 'hidden';

    // Pre-select any variants already in the cart so they show as checked and are
    // included in the count (they stay locked — see toggleVariantSelection).
    this.syncInCartSelection();

    // Sync the main PDP price/button with whatever is currently checked in the popup,
    // so opening the popup with a pre-selected variant immediately reflects the price.
    this.updatePriceDisplay();
    this.updateMainPriceAndButton();
  }

  close() {
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
    }
    if (this.popup) {
      this.popup.classList.remove('is-active');
    }
    document.body.style.overflow = '';
  }

  isOpen() {
    return this.popup && this.popup.classList.contains('is-active');
  }

  applySelection() {
    if (this.selectedVariants.length === 0) return;

    // Only newly-picked dates are sent to add-to-cart and contribute to the price
    // (re-adding in-cart dates would duplicate them). The full count (including
    // in-cart) is shown on the card.
    const activeVariants = this.getActiveVariants();
    // Every picked date is already in the cart — nothing new to apply. Just close
    // the popup; the main button correctly stays in its "ADDED TO CART" state.
    if (activeVariants.length === 0) {
      this.close();
      return;
    }

    const totalPrice = activeVariants.reduce((sum, variant) => sum + variant.price, 0);

    // Pass selected variants to parent variant selector
    this.variantSelector.applySelectedDates(
      activeVariants.map(v => ({ id: v.id, price: v.price, title: v.title })),
      totalPrice,
      this.selectedVariants.length
    );

    this.close();
  }
}

class ProductImageSlider {
  constructor(el) {
    this.el = el;
    this.swiperContainer = el.querySelector('.product-image-slider__swiper');
    this.pagination = el.querySelector('[data-product-image-pagination]');

    if (!this.swiperContainer) return;

    this.swiper = new Swiper(this.swiperContainer, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      pagination: {
        el: this.pagination,
        clickable: true,
        bulletClass: 'product-image-slider__bullet',
        bulletActiveClass: 'product-image-slider__bullet--active'
      }
    });
  }
}

class ProductDetail {
  constructor() {
    this.sections = document.querySelectorAll('.product-detail');
    if (!this.sections.length) return;

    this.sections.forEach((section) => {
      this.initAccordions(section);
      this.initReadMore(section);
      this.initVariantSelector(section);
      this.initImageSliders(section);
    });
  }

  initAccordions(section) {
    section.querySelectorAll('[data-accordion]').forEach((el) => {
      new ProductDetailAccordion(el);
    });
  }

  initReadMore(section) {
    section.querySelectorAll('[data-readmore]').forEach((el) => {
      new ProductDetailReadMore(el);
    });
  }

  initVariantSelector(section) {
    section.variantSelectorInstance = new ProductDetailVariantSelector(section);
  }

  initImageSliders(section) {
    section.querySelectorAll('[data-product-image-slider]').forEach((el) => {
      new ProductImageSlider(el);
    });
  }

}

/**
 * CartStateManager
 * Fetches /cart.js on page load and disables add-to-cart buttons / variant options
 * for products/variants already in the cart.
 */
class CartStateManager {
  constructor() {
    this.cartVariantIds = new Set();
    this.cartItems = [];
    this.conversationLabCount = 0;
    this.knownConversationLabVariantIds = null;
    // First state pass = page load. Used to auto-advance a default selection that's
    // already in the cart, without doing it on later cart updates.
    this.isFirstApply = true;
    this.init();
  }

  init() {
    this.fetchCartAndApply();

    // Re-check after any add-to-cart
    document.addEventListener('cart:updated', () => {
      this.fetchCartAndApply();
    });

    // Also follow the theme's Dawn pub/sub cart channel — the same signal the
    // header cart bubble reacts to — so cart changes made by any Dawn component
    // on this page (quantity edits, removals) re-sync the PDP state too.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.fetchCartAndApply();
      });
    }

    // Listen for variant selection clicks to re-check button state
    document.addEventListener('click', (e) => {
      // Store variant option clicked
      const storeOption = e.target.closest('.store-variant-selector__option');
      if (storeOption) {
        setTimeout(() => this.applyStoreVariantStates(), 10);
        return;
      }

      // Gift card amount clicked
      const giftBtn = e.target.closest('.gift-card-pdp__amount-btn');
      if (giftBtn) {
        setTimeout(() => this.applyGiftCardStates(), 10);
        return;
      }

      // Conversation lab variant option clicked
      const variantOption = e.target.closest('[data-variant-option]');
      if (variantOption) {
        setTimeout(() => this.applyConversationLabStates(), 10);
        return;
      }

      // Conversation lab PDP variant (radio) clicked — re-evaluate the single
      // add-to-cart button so it reads ADD TO CART for a not-yet-added variant
      // and only stays ADDED TO CART when the selected variant is in the cart.
      const clpVariant = e.target.closest('.conversation-lab-pdp__variant');
      if (clpVariant) {
        setTimeout(() => this.applySingleVariantStates(), 10);
      }
    });
  }

  fetchCartAndApply() {
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then((res) => res.json())
      .then((cart) => {
        this.cartVariantIds = new Set(cart.items.map((item) => String(item.id)));
        this.cartItems = cart.items;
        this.computeConversationLabCount();
        document.dispatchEvent(new CustomEvent('cart:conversation-lab-count-updated'));
        this.applyAllStates();
      })
      .catch((err) => console.error('CartStateManager: failed to fetch cart', err));
  }

  // Variant IDs known to be conversation-lab: every conversation-lab variant on
  // this page, plus the conversation-lab line items already in the cart at page
  // load (rendered server-side via template_suffix, so items added from other
  // conversation-lab PDPs are recognised too).
  getKnownConversationLabVariantIds() {
    if (this.knownConversationLabVariantIds) return this.knownConversationLabVariantIds;
    const ids = new Set();
    document.querySelectorAll('[data-conversation-lab-section]').forEach((section) => {
      const serverIds = section.getAttribute('data-cl-cart-variant-ids');
      if (serverIds) {
        try {
          JSON.parse(serverIds).forEach((id) => ids.add(String(id)));
        } catch (error) { /* ignore malformed */ }
      }
      section.querySelectorAll('[data-variant-id]').forEach((el) => {
        const variantId = el.getAttribute('data-variant-id');
        if (variantId) ids.add(String(variantId));
      });
    });
    this.knownConversationLabVariantIds = ids;
    return ids;
  }

  // Total quantity of conversation-lab sessions in the cart (across all
  // conversation-lab PDPs) — the figure package pricing is based on.
  computeConversationLabCount() {
    const knownIds = this.getKnownConversationLabVariantIds();
    // Variants share a product: any cart line whose variant id is known-CL marks
    // its product_id as conversation-lab, so sibling dates of that product added
    // after this page rendered (e.g. on another PDP, seen again via bfcache)
    // still count toward package pricing.
    const clProductIds = new Set(
      this.cartItems
        .filter((item) => knownIds.has(String(item.id)))
        .map((item) => item.product_id)
    );
    this.conversationLabCount = this.cartItems
      .filter((item) => knownIds.has(String(item.id)) || clProductIds.has(item.product_id))
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  getConversationLabCount() {
    return this.conversationLabCount;
  }

  applyAllStates() {
    this.applySingleVariantStates();
    this.applyGiftCardStates();
    this.applyStoreVariantStates();
    this.applyConversationLabStates();
    this.isFirstApply = false;
  }

  // Single-variant PDPs (any .product-detail without a multi-variant selector)
  applySingleVariantStates() {
    document.querySelectorAll('.product-detail').forEach((section) => {
      // Skip multi-variant sections
      if (section.querySelector('[data-variant-selector]')) return;
      if (section.querySelector('[data-store-variant-selector]')) return;
      if (section.querySelector('[data-gift-card-amounts]')) return;

      // Mark conversation-lab PDP variant rows that are already in the cart so
      // they show as added and can't be re-selected (locked via .is-in-cart).
      section.querySelectorAll('.conversation-lab-pdp__variant').forEach((variant) => {
        const variantId = variant.getAttribute('data-clp-variant-id');
        if (variantId && this.cartVariantIds.has(String(variantId))) {
          variant.classList.add('is-in-cart');
        } else {
          variant.classList.remove('is-in-cart');
        }
      });

      const btn = section.querySelector('.product-detail__register-btn');
      if (!btn) return;

      const selectedAttr = btn.getAttribute('data-selected-variants');
      if (!selectedAttr) return;

      try {
        const items = JSON.parse(selectedAttr);
        if (items.length === 1 && this.cartVariantIds.has(String(items[0].id))) {
          this.disableButton(btn, 'ADDED TO CART');
        } else {
          this.enableButton(btn, 'ADD TO CART');
        }
      } catch (e) { /* ignore */ }
    });
  }

  // Gift Card Amounts
  applyGiftCardStates() {
    document.querySelectorAll('.gift-card-pdp').forEach((section) => {
      const amountBtns = section.querySelectorAll('.gift-card-pdp__amount-btn');

      amountBtns.forEach((btn) => {
        const variantId = btn.getAttribute('data-gift-variant-id');
        if (variantId && this.cartVariantIds.has(String(variantId))) {
          btn.classList.add('is-in-cart');
        } else {
          btn.classList.remove('is-in-cart');
        }
      });

      // Reflect the currently selected amount on the submit button: ADDED TO CART
      // when that amount is already in the cart, ADD TO CART otherwise.
      const selectedBtn = section.querySelector('.gift-card-pdp__amount-btn.is-selected');
      if (selectedBtn) {
        const variantId = selectedBtn.getAttribute('data-gift-variant-id');
        const submitBtn = section.querySelector('.gift-card-pdp__add-to-cart-btn');
        if (submitBtn && variantId) {
          if (this.cartVariantIds.has(String(variantId))) {
            this.disableButton(submitBtn, 'ADDED TO CART');
          } else {
            this.enableButton(submitBtn, 'ADD TO CART');
          }
        }
      }
    });
  }

  // Store Product Variants
  applyStoreVariantStates() {
    document.querySelectorAll('.store-page-main-container').forEach((section) => {
      const options = section.querySelectorAll('.store-variant-selector__option');
      const btn = section.querySelector('.product-detail__register-btn');

      options.forEach((option) => {
        const variantId = option.getAttribute('data-store-variant-id');
        if (variantId && this.cartVariantIds.has(String(variantId))) {
          option.classList.add('is-in-cart');
        } else {
          option.classList.remove('is-in-cart');
        }
      });

      if (btn) {
        const selectedOption = section.querySelector('.store-variant-selector__option.is-selected');
        if (selectedOption) {
          const variantId = selectedOption.getAttribute('data-store-variant-id');
          if (variantId && this.cartVariantIds.has(String(variantId))) {
            this.disableButton(btn, 'ADDED TO CART');
          } else {
            this.enableButton(btn, 'ADD TO CART');
          }
        }
      }
    });
  }

  // Conversation Lab
  applyConversationLabStates() {
    document.querySelectorAll('[data-conversation-lab-section]').forEach((section) => {
      const btn = section.querySelector('.product-detail__register-btn');

      // Next-available option
      const nextAvailable = section.querySelector('[data-variant-option="next-available"]');
      if (nextAvailable) {
        const variantId = nextAvailable.getAttribute('data-variant-id');
        if (variantId && this.cartVariantIds.has(String(variantId))) {
          nextAvailable.classList.add('is-in-cart');
        } else {
          nextAvailable.classList.remove('is-in-cart');
        }
      }

      // On page load, if the default (next-available) selection is already in the
      // cart it's locked and can't be re-added — move selection to the pick-dates
      // option so the shopper isn't stuck on a disabled option. First pass only:
      // doing it on every cart update would yank the UI off the "ADDED TO CART"
      // confirmation right after the shopper adds this class.
      if (this.isFirstApply && nextAvailable &&
          nextAvailable.classList.contains('is-in-cart') &&
          nextAvailable.classList.contains('is-selected')) {
        const pickDates = section.querySelector('[data-variant-option="pick-dates"]');
        if (pickDates && section.variantSelectorInstance) {
          section.variantSelectorInstance.selectOption(pickDates);
        }
      }

      // Date picker variant items. A date that had is-in-cart but is no longer
      // in the cart was removed on the cart page — deselect it everywhere.
      const removedIds = new Set();
      const datePickerVariants = section.querySelectorAll('[data-date-picker-variants] [data-variant-id]');
      datePickerVariants.forEach((item) => {
        const variantId = item.getAttribute('data-variant-id');
        if (variantId && this.cartVariantIds.has(String(variantId))) {
          item.classList.add('is-in-cart');
        } else {
          if (item.classList.contains('is-in-cart')) {
            removedIds.add(String(variantId));
            item.classList.remove('is-selected');
          }
          item.classList.remove('is-in-cart');
        }
      });

      // Purge removed dates from the popup's live selection and refresh its totals.
      const datePicker = section.variantSelectorInstance && section.variantSelectorInstance.datePicker;
      if (removedIds.size > 0 && datePicker && datePicker.selectedVariants) {
        datePicker.selectedVariants = datePicker.selectedVariants.filter(
          (v) => !removedIds.has(String(v.id))
        );
        datePicker.updatePriceDisplay();
        datePicker.updateMonthTabsDots();
      }

      // Reconcile the applied pick-dates selection with the live cart: drop
      // removed dates, and keep in-cart ids out of the add payload so a re-click
      // can't add duplicates. Re-price from the dates still pending.
      const pickDatesOption = section.querySelector('[data-variant-option="pick-dates"]');
      let pendingPickedCount = 0;
      if (pickDatesOption) {
        const idsAttr = pickDatesOption.getAttribute('data-variant-ids');
        if (idsAttr) {
          try {
            const appliedIds = JSON.parse(idsAttr).filter((id) => !removedIds.has(String(id)));
            const pendingIds = appliedIds.filter((id) => !this.cartVariantIds.has(String(id)));
            pendingPickedCount = pendingIds.length;
            const hasInCartIds = pendingIds.length !== appliedIds.length;
            if (removedIds.size > 0 || (pendingIds.length > 0 && hasInCartIds)) {
              const priceByVariantId = new Map();
              datePickerVariants.forEach((item) => {
                priceByVariantId.set(
                  item.getAttribute('data-variant-id'),
                  parseInt(item.getAttribute('data-variant-price'), 10) || 0
                );
              });
              const pendingTotal = pendingIds.reduce(
                (sum, id) => sum + (priceByVariantId.get(String(id)) || 0), 0
              );
              // Payload: pending dates if any; otherwise the applied dates still
              // in the cart so the button keeps its ADDED TO CART state.
              const payloadIds = pendingIds.length > 0 ? pendingIds : appliedIds;
              pickDatesOption.setAttribute('data-variant-ids', JSON.stringify(appliedIds));
              pickDatesOption.setAttribute('data-total-price', pendingTotal);
              const cardPrice = pickDatesOption.querySelector('.variant-selector__price');
              if (cardPrice) cardPrice.textContent = formatMoney(pendingTotal);
              if (pickDatesOption.classList.contains('is-selected')) {
                if (btn) {
                  btn.setAttribute('data-selected-variants',
                    JSON.stringify(payloadIds.map((id) => ({ id: id, quantity: 1 })))
                  );
                }
                const priceDisplay = section.querySelector('.product-detail__price-block .product-detail__price-sale');
                if (priceDisplay) priceDisplay.textContent = formatMoney(pendingTotal);
              }
            }
          } catch (e) {
            console.warn('CartStateManager: invalid data-variant-ids, resetting', e);
            pickDatesOption.setAttribute('data-variant-ids', '[]');
            pendingPickedCount = 0;
          }
        }
      }

      // "N DATE(S) SELECTED" = in-cart dates + applied dates pending add.
      if (pickDatesOption) {
        const selectDatesLink = pickDatesOption.querySelector('[data-select-dates-trigger]');
        const inCartDateCount = section.querySelectorAll(
          '[data-date-picker-variants] [data-variant-id].is-in-cart'
        ).length;
        const totalDateCount = inCartDateCount + pendingPickedCount;
        if (selectDatesLink) {
          selectDatesLink.textContent = totalDateCount > 0
            ? countLabel(totalDateCount, 'DATE', 'DATES')
            : 'CHOOSE DATES';
        }
      }

      // Check currently selected option for button state
      if (btn) {
        const selectedOption = section.querySelector('[data-variant-option].is-selected');
        if (selectedOption) {
          const optionType = selectedOption.getAttribute('data-variant-option');

          if (optionType === 'next-available') {
            const variantId = selectedOption.getAttribute('data-variant-id');
            if (variantId && this.cartVariantIds.has(String(variantId))) {
              this.disableButton(btn, 'ADDED TO CART');
            } else {
              this.enableButton(btn, 'ADD TO CART');
            }
          } else if (optionType === 'pick-dates') {
            const selectedAttr = btn.getAttribute('data-selected-variants');
            if (selectedAttr) {
              try {
                const items = JSON.parse(selectedAttr);
                const allInCart = items.length > 0 && items.every((item) =>
                  this.cartVariantIds.has(String(item.id))
                );
                if (allInCart) {
                  this.disableButton(btn, 'ADDED TO CART');
                } else {
                  this.enableButton(btn, 'ADD TO CART');
                  // Nothing left to add (e.g. every applied date was removed
                  // from the cart) — keep the button unclickable.
                  if (items.length === 0) btn.classList.add('is-disabled');
                }
              } catch (e) { /* ignore */ }
            }
          }
        }
      }
    });
  }

  disableButton(btn, text) {
    btn.classList.add('is-disabled');
    btn.classList.add('is-added-to-cart');
    const textEl = btn.querySelector('.button__text');
    if (textEl) textEl.textContent = text;
    const arrowEl = btn.querySelector('.button__icon');
    if (arrowEl) arrowEl.style.display = 'none';
  }

  enableButton(btn, text) {
    if (!btn.classList.contains('is-added-to-cart')) return;
    btn.classList.remove('is-disabled');
    btn.classList.remove('is-added-to-cart');
    const textEl = btn.querySelector('.button__text');
    if (textEl) textEl.textContent = text;
    const arrowEl = btn.querySelector('.button__icon');
    if (arrowEl) arrowEl.style.display = '';
  }
}

// Update the header cart count bubbles to the latest item count.
// Supports the header-v2 cart link, the default Dawn cart bubble, and any
// generic [data-cart-count] element so every header variant stays in sync.
function updateCartCount(itemCount) {
  const count = parseInt(itemCount, 10) || 0;

  // header-v2 cart link: create the count badge if it doesn't exist yet
  document.querySelectorAll('.header-v2__cart-link').forEach((link) => {
    let countEl = link.querySelector('.header-v2__cart-count');
    if (count > 0) {
      if (!countEl) {
        const wrap = link.querySelector('.header-v2__cart-icon-wrap') || link;
        countEl = document.createElement('span');
        countEl.className = 'header-v2__cart-count';
        countEl.setAttribute('aria-hidden', 'true');
        wrap.appendChild(countEl);
      }
      countEl.textContent = count;
    } else if (countEl) {
      countEl.remove();
    }
    link.setAttribute('aria-label', 'Cart (' + count + ' items)');
  });

  // Default Dawn cart bubble
  document.querySelectorAll('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
    el.textContent = count < 100 ? count : '';
  });

  // Generic hook used elsewhere on the page
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
  });
}

// Show the "The item has been added to the cart" message anchored under the
// header cart link, then auto-hide it. Falls back to a fixed toast if the
// header cart link isn't present on the page.
function showAddedToCartMessage() {
  const message = 'The item has been added to the cart';
  const anchor =
    document.querySelector('.header-v2__cart-link') ||
    document.querySelector('#cart-icon-bubble');

  // Make sure the header (and therefore the message anchored to it) is visible.
  // If it's a hide-on-scroll header it gets revealed; a non-sticky header
  // scrolls back into view.
  const headerEl = document.querySelector('header-v2');
  if (headerEl && typeof headerEl.reveal === 'function') {
    headerEl.reveal();
  }

  // Remove any existing toast so repeated clicks reset the timer
  document.querySelectorAll('.added-to-cart-toast').forEach((el) => el.remove());

  const toast = document.createElement('div');
  toast.className = 'added-to-cart-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.textContent = message;

  if (anchor) {
    anchor.classList.add('has-added-to-cart-toast');
    anchor.appendChild(toast);
  } else {
    toast.classList.add('added-to-cart-toast--fixed');
    document.body.appendChild(toast);
  }

  // Trigger the enter transition
  requestAnimationFrame(() => toast.classList.add('is-visible'));

  const hide = () => {
    toast.classList.remove('is-visible');
    toast.addEventListener('transitionend', () => {
      if (anchor) anchor.classList.remove('has-added-to-cart-toast');
      toast.remove();
    }, { once: true });
  };

  // Only start the auto-hide countdown once the anchor is actually on screen.
  // If the page is mid-scroll (non-sticky header scrolling back to top), the
  // message stays put until the header is visible, then hides after the delay.
  const startHideTimer = () => setTimeout(hide, 2500);
  const anchorInView = () => {
    if (!anchor) return true;
    const rect = anchor.getBoundingClientRect();
    return rect.top >= 0 && rect.top < window.innerHeight;
  };

  if (anchorInView()) {
    startHideTimer();
  } else {
    let settled = false;
    const onScrollSettled = () => {
      if (settled || !anchorInView()) return;
      settled = true;
      window.removeEventListener('scroll', onScrollSettled);
      startHideTimer();
    };
    window.addEventListener('scroll', onScrollSettled, { passive: true });
    // Safety fallback in case the scroll event never fires the in-view check
    setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('scroll', onScrollSettled);
      startHideTimer();
    }, 1200);
  }
}

/**
 * Agreement checkboxes gate
 * When a consent group is marked `data-consent-required`, the Add to Cart button
 * in the same product card stays locked until every checkbox in the group is ticked.
 * Uses event delegation so it also works after a theme-editor section re-render.
 */
function syncConsentGate(group) {
  if (!group) return;

  const scope = group.closest('.product-detail__details') || group.closest('.product-detail');
  const btn = scope && scope.querySelector('.product-detail__register-btn');
  if (!btn) return;

  const boxes = group.querySelectorAll('[data-consent-check]');
  const required = group.hasAttribute('data-consent-required') && boxes.length > 0;

  if (!required) {
    btn.classList.remove('is-consent-locked');
    btn.removeAttribute('aria-disabled');
    return;
  }

  const allChecked = Array.from(boxes).every((box) => box.checked);
  btn.classList.toggle('is-consent-locked', !allChecked);
  btn.setAttribute('aria-disabled', allChecked ? 'false' : 'true');
}

function syncAllConsentGates() {
  document.querySelectorAll('[data-consent-group]').forEach(syncConsentGate);
}

document.addEventListener('change', (e) => {
  const box = e.target && e.target.closest && e.target.closest('[data-consent-check]');
  if (!box) return;
  syncConsentGate(box.closest('[data-consent-group]'));
});

// Browsers restore checkbox state on reload / back-forward, so re-sync on both.
document.addEventListener('DOMContentLoaded', syncAllConsentGates);
window.addEventListener('pageshow', syncAllConsentGates);
document.addEventListener('shopify:section:load', syncAllConsentGates);

/**
 * Policy accordions that ship open.
 * ProductDetailAccordion drives the open/close animation (max-height transition);
 * this only puts the row into the same "open" state it would be in after a click,
 * so the first close animates from a real pixel height instead of jumping.
 * Registered after ProductDetail is constructed so the click handler is already bound.
 */
function openDefaultAccordions(root) {
  (root || document).querySelectorAll('[data-accordion][data-accordion-open]').forEach((row) => {
    const trigger = row.querySelector('[data-accordion-trigger]');
    const content = row.querySelector('[data-accordion-content]');
    if (!trigger || !content) return;

    // Suppress the transition for this first paint so the row is simply open on
    // load instead of sliding open; clicks after that animate normally.
    const icon = trigger.querySelector('.product-detail__accordion-icon');
    content.style.transition = 'none';
    if (icon) icon.style.transition = 'none';

    trigger.setAttribute('aria-expanded', 'true');
    content.classList.add('is-open');
    content.style.maxHeight = content.scrollHeight + 'px';

    // Force reflow so the open state is committed before transitions come back.
    // eslint-disable-next-line no-unused-expressions
    content.offsetHeight;
    requestAnimationFrame(() => {
      content.style.transition = '';
      if (icon) icon.style.transition = '';
    });
  });
}

// An open accordion is pinned to the pixel height it had when it opened, so text
// that re-wraps on resize would get clipped. Re-measure the open ones instead.
let policyResizeTimer = null;
window.addEventListener('resize', () => {
  clearTimeout(policyResizeTimer);
  policyResizeTimer = setTimeout(() => {
    document.querySelectorAll('.product-detail__policy-item [data-accordion-content].is-open').forEach((content) => {
      content.style.maxHeight = 'none';
      const target = content.scrollHeight;
      content.style.maxHeight = target + 'px';
    });
  }, 150);
});

// Reusable Add to Cart - works anywhere via data-selected-variants attribute
// Uses document-level event delegation so it works on any .product-detail__register-btn button
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.product-detail__register-btn');
  if (!btn) return;
  if (btn.classList.contains('is-disabled')) return;
  if (btn.classList.contains('is-consent-locked')) return;
  if (btn.classList.contains('loading')) return;

  const selectedVariantsAttr = btn.getAttribute('data-selected-variants');
  if (!selectedVariantsAttr) return;

  let items;
  try {
    items = JSON.parse(selectedVariantsAttr);
  } catch (err) {
    console.error('Invalid data-selected-variants JSON:', err);
    return;
  }

  if (!Array.isArray(items) || items.length === 0) return;

  // Build cart items payload - each item needs id and quantity
  const cartItems = items.map((item) => ({
    id: parseInt(item.id, 10),
    quantity: parseInt(item.quantity, 10) || 1
  }));

  // Show loading spinner (uses existing .button.loading CSS from buttons.css)
  btn.classList.add('loading');

  // Add items to Shopify cart via AJAX API
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: cartItems })
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((data) => {
          throw new Error(data.description || 'Could not add to cart');
        });
      }
      return response.json();
    })
    .then(() => {
      // Update cart count in header, then show the "added to cart" message.
      // No redirect to the cart page — the shopper stays on the product page.
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          updateCartCount(cart.item_count);

          // Dispatch custom event so other components can react
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .finally(() => {
          showAddedToCartMessage();
        });
    })
    .catch((error) => {
      console.error('Add to cart error:', error);
      alert(error.message || 'Something went wrong. Please try again.');
    })
    .finally(() => {
      btn.classList.remove('loading');
    });
});

document.addEventListener('DOMContentLoaded', () => {
  new ProductDetail();
  window.cartStateManager = new CartStateManager();
  openDefaultAccordions();
});

// A theme-editor re-render replaces the markup without re-running ProductDetail,
// so re-bind the policy accordions in the reloaded section before opening them.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.product-detail__policy-item[data-accordion]').forEach((el) => {
    new ProductDetailAccordion(el);
  });
  openDefaultAccordions(event.target);
});

// Back/forward navigation restores the page with stale cart-dependent UI —
// via bfcache (event.persisted) or via the HTTP cache (navigation type
// 'back_forward'). Re-sync from /cart.js in both cases instead of reloading;
// worst case it's one redundant cart fetch.
window.addEventListener('pageshow', (event) => {
  if (!window.cartStateManager) return;
  const navEntry = performance.getEntriesByType('navigation')[0];
  const isBackForward = navEntry && navEntry.type === 'back_forward';
  if (event.persisted || isBackForward) {
    window.cartStateManager.fetchCartAndApply();
  }
});
